import type { SupportCategory, SupportCategoryOption } from "@/services/supportService";
import type { TicketStatus } from "@/services/ticketsService";

export const STATUSES: { key: TicketStatus; label: string; style: string; dot: string }[] = [
  { key: "not_started", label: "Not started", style: "border-border bg-muted/60 text-muted-foreground", dot: "bg-muted-foreground" },
  { key: "in_progress", label: "In progress", style: "border-coral/40 bg-coral-light text-coral-dark", dot: "bg-coral" },
  { key: "blocked", label: "Blocked", style: "border-destructive/40 bg-destructive/10 text-destructive", dot: "bg-destructive" },
  { key: "in_review", label: "In review", style: "border-teal/40 bg-teal-light text-teal-dark", dot: "bg-teal" },
  { key: "done", label: "Done", style: "border-teal/50 bg-teal-light text-teal-dark", dot: "bg-teal-dark" },
];

/** Badge style per category. Brand tokens only (coral = primary accent,
 *  teal = neutral); legacy technical shares Web Dev's style. */
export const TEAM_STYLE: Record<string, string> = {
  technical: "border-coral/40 text-coral-dark",
  web_dev: "border-coral/40 text-coral-dark",
  ai: "border-coral/40 text-coral",
  seo: "border-teal/50 text-teal-dark",
  ads: "border-teal/50 text-teal",
  communications: "border-border text-muted-foreground",
};

/** Dot colour on a ticket card's category badge. */
export const TEAM_DOT: Record<string, string> = {
  technical: "bg-coral",
  web_dev: "bg-coral",
  ai: "bg-coral-dark",
  seo: "bg-teal",
  ads: "bg-teal-dark",
  communications: "bg-muted-foreground",
};

/** The ONE short label map for categories (badges, filters), as shown with
 *  routing ON: legacy "technical" reads as Web Dev (the backend's
 *  CATEGORY_LABELS). With routing OFF, categoryLabel() keeps today's
 *  "Technical". */
export const CATEGORY_LABELS: Record<SupportCategory, string> = {
  web_dev: "Web Dev",
  ai: "AI",
  seo: "SEO",
  ads: "Ads",
  communications: "Communications",
  technical: "Web Dev",
};

export function categoryLabel(key: string | null | undefined, routingEnabled = false): string {
  if (key === "technical" && !routingEnabled) return "Technical";
  return (key && CATEGORY_LABELS[key as SupportCategory]) || key || "—";
}

/** The five departments, in display order (filters list these; legacy
 *  technical rows are matched by Web Dev). */
export const DEPARTMENT_KEYS: SupportCategory[] = ["web_dev", "ai", "seo", "ads", "communications"];

export const PRIO_STYLE: Record<string, string> = {
  high: "text-destructive",
  medium: "text-coral-dark",
  low: "text-muted-foreground",
};

/** Stored category → the key filters and sorting use. With routing ON legacy
 *  technical folds into web_dev; with routing OFF it stays technical. */
export function normalizeCategory(
  key: string | null | undefined,
  routingEnabled = false,
): string {
  return routingEnabled && key === "technical" ? "web_dev" : key ?? "";
}

/** Table filter options. Routing OFF: today's Technical and SEO. Routing ON:
 *  the five departments. */
export function categoryFilterOptions(routingEnabled: boolean): { value: string; label: string }[] {
  const keys: SupportCategory[] = routingEnabled ? DEPARTMENT_KEYS : ["technical", "seo"];
  return keys.map((key) => ({ value: key, label: categoryLabel(key, routingEnabled) }));
}

/** Long option text + hint for the new-ticket form, keyed by category. Which
 *  keys are offered comes from GET /support/categories (routing flag). The
 *  technical/seo wording is unchanged from before department routing. */
export const CATEGORY_OPTION_TEXT: Record<SupportCategory, { label: string; hint: string }> = {
  technical: {
    label: "Technical — something isn't working",
    hint: "Routed to our dev/AI team (a feature not loading, an error, a bug).",
  },
  seo: {
    label: "SEO task or content request",
    hint: "Routed to our SEO team (content, keywords, on-page fixes).",
  },
  web_dev: {
    label: "Web Dev — the site or app is broken, or needs a change",
    hint: "Routed to our Web Dev team (errors, broken pages, site changes).",
  },
  ai: {
    label: "AI — AI tools or automation",
    hint: "Routed to our AI team (AI features, automations, chatbots).",
  },
  ads: {
    label: "Ads — Google, Meta or LSA ads",
    hint: "Routed to our Ads team (campaigns, budgets, ad performance).",
  },
  communications: {
    label: "Communications — client comms or reporting",
    hint: "Routed to our Communications team (reports, updates, messaging).",
  },
};

/** Used when GET /support/categories fails: today's two options (routing
 *  off, so technical keeps its "Technical" label, as the API returns it). */
export const FALLBACK_CATEGORIES: SupportCategoryOption[] = [
  { key: "technical", label: "Technical" },
  { key: "seo", label: "SEO" },
];

/** A category proposed by the AI draft (or a caller's seed) resolved against
 *  what is on offer: legacy technical → web_dev when routing is on; anything
 *  not offered → "" so the form shows its placeholder, never a blank Select. */
export function resolveOfferedCategory(
  value: string | null | undefined,
  offered: SupportCategory[],
  routingEnabled: boolean,
): SupportCategory | "" {
  if (!value) return "";
  const mapped = routingEnabled && value === "technical" ? "web_dev" : value;
  return offered.includes(mapped as SupportCategory) ? (mapped as SupportCategory) : "";
}

export function statusMeta(key: string) {
  return STATUSES.find((s) => s.key === key) ?? STATUSES[0];
}
